import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { useEstateContext } from '@/lib/EstateContext';

/**
 * Hook to check estate subscription status for estate managers.
 * Uses activeEstateId from EstateContext so it tracks whichever estate
 * the EM currently has selected in the sidebar switcher.
 */
export function useSubscription() {
  const { user } = useAuth();
  const { activeEstateId } = useEstateContext();

  // Prefer the active estate from context; fall back to the legacy FK for
  // components that render before EstateContext is ready (e.g. first load)
  const estateId = activeEstateId || user?.estate_id;

  const { data: subscription, isLoading, error } = useQuery({
    queryKey: ['subscription', estateId],
    queryFn: async () => {
      if (!estateId) return null;

      const { data, error } = await supabase
        .from('estates')
        .select('unit_count, subscription_tier, subscription_status, trial_starts_at, trial_ends_at, grace_ends_at, paystack_customer_code, paystack_subscription_code')
        .eq('id', estateId)
        .single();

      if (error) throw error;
      
      // Calculate days remaining
      const now = new Date();
      let daysRemaining = 0;
      let expiresAt = null;
      
      if (data.subscription_status === 'trial' && data.trial_ends_at) {
        expiresAt = new Date(data.trial_ends_at);
        daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
      } else if (data.subscription_status === 'grace' && data.grace_ends_at) {
        expiresAt = new Date(data.grace_ends_at);
        daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
      }
      
      // Determine if user has write access
      // Active subscriptions always have access (they're paying!)
      // Past_due subscriptions keep access (payment retrying, don't punish user)
      // Grace period = READ-ONLY (payment failed, retries exhausted)
      // Trial needs to check if days remaining > 0
      const hasWriteAccess = 
        data.subscription_status === 'active' || 
        data.subscription_status === 'past_due' || // Payment failed but retrying - keep access
        (data.subscription_status === 'trial' && daysRemaining > 0); // Grace excluded - read-only mode
      
      // Get pricing info
      const pricing = getPricingForTier(data.subscription_tier);
      
      return {
        ...data,
        daysRemaining: Math.max(0, daysRemaining),
        expiresAt,
        hasWriteAccess,
        isInTrial: data.subscription_status === 'trial',
        isInGrace: data.subscription_status === 'grace',
        isExpired: data.subscription_status === 'expired',
        isActive: data.subscription_status === 'active',
        isPastDue: data.subscription_status === 'past_due',
        pricing,
      };
    },
    enabled: !!estateId && user?.user_role === 'estate_manager',
    staleTime: 1000 * 30, // 30 seconds - shorter for faster updates after payment
    refetchOnWindowFocus: true, // Refetch when user returns from Paystack
  });

  return {
    subscription,
    isLoading,
    error,
  };
}

function getPricingForTier(tier) {
  const pricing = {
    small: { amount: 599, label: 'R599/month', units: 'Up to 50 units' },
    medium: { amount: 1149, label: 'R1,149/month', units: '51-150 units' },
    large: { amount: 1999, label: 'R1,999/month', units: '151-300 units' },
    custom: { amount: null, label: 'Custom pricing', units: '300+ units' },
  };
  
  return pricing[tier] || pricing.small;
}
